import { useEffect, useState } from "react"
import { usePage } from "@inertiajs/react"
import { useInertiaCable } from "@inertia-cable/react"
import { CheckCircle2, Info, X, XCircle } from "lucide-react"
import { cn } from "@/lib/cn"
import { ChatMessageBubble } from "@/components/domain/chat-message-bubble"
import { ForgeOnFailure } from "@/components/domain/forge-on-failure"
import { JsonEditor } from "@/components/ui/json-editor"
import { Dialog } from "@/components/ui/dialog"
import type { SharedProps } from "@/types/inertia"
import type { ChatBubble, TestLabAssertionVerdict, TestLabScenario, TestRunDetail } from "@/types/test_lab"

type Props = {
  skillId: number                    // parent Skill id (route segment is /skills/:skill_id/test_runs/:run_id)
  scenario: TestLabScenario
  testStream: string
  onFix: () => Promise<void> | void  // wraps TestLab's discussFailure(runId)
}

type DetailTab = "conversation" | "assertions" | "run_details" | "mocks" | "notes"

const TABS: { id: DetailTab; label: string }[] = [
  { id: "conversation", label: "Conversation" },
  { id: "assertions",   label: "Assertions"   },
  { id: "run_details",  label: "Run details"  },
  { id: "mocks",        label: "Mocks"        },
  { id: "notes",        label: "Notes"        },
]

export function ScenarioDetail({ skillId, scenario, testStream, onFix }: Props) {
  const [tab, setTab]       = useState<DetailTab>("conversation")
  const [run, setRun]       = useState<TestRunDetail | null>(null)
  const [loading, setLoad]  = useState(false)

  const runId = scenario.latestRunId

  // Fetch the run detail (transcript + verdicts + cost) whenever the
  // selected scenario's latest run id changes. The list pre-loads the
  // scenario's metadata; the detail body needs the message rows.
  useEffect(() => {
    if (runId == null) { setRun(null); return }
    let cancelled = false
    setLoad(true)
    fetch(`/skills/${skillId}/test_runs/${runId}`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data: TestRunDetail = await res.json()
        if (!cancelled) setRun(data)
      })
      .catch(() => { if (!cancelled) setRun(null) })
      .finally(() => { if (!cancelled) setLoad(false) })
    return () => { cancelled = true }
  }, [runId, skillId])

  // Re-fetch when the run updates (RunOneWorker → InertiaCable broadcast).
  useInertiaCable(testStream, {
    debounce: 250,
    onRefresh: (payload) => {
      if (payload.model !== "TestRun") return
      if (runId == null) return
      fetch(`/skills/${skillId}/test_runs/${runId}`, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      })
        .then((r) => r.ok ? r.json() : null)
        .then((data) => { if (data) setRun(data) })
        .catch(() => {/* keep last good */})
    },
  })

  const failed = scenario.latestRunStatus === "failed" || scenario.latestRunStatus === "errored"
  const messages = run?.messages ?? []
  const verdicts = run?.verdicts.length ? run.verdicts : scenario.latestRunVerdicts

  return (
    <div className="flex h-full flex-col rounded-card border border-border bg-app-panel">
      <Header scenario={scenario} />

      <div className="border-b border-border px-4">
        <div className="flex gap-1">
          {TABS.map((t) => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)}
              className={cn(
                "border-b-2 px-3 py-2 text-sm transition",
                tab === t.id
                  ? "border-brand-red text-app-text"
                  : "border-transparent text-app-muted hover:text-app-text"
              )}>
              {t.label}
              {t.id === "assertions" && verdicts.length > 0 && (() => {
                const passed = verdicts.filter((v) => v.passed).length
                const allPassed = passed === verdicts.length
                return (
                  <span
                    className={cn(
                      "ml-1.5 text-[11px] font-medium",
                      allPassed ? "text-status-success" : "text-status-error",
                    )}
                  >
                    {passed}/{verdicts.length}
                  </span>
                )
              })()}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {tab === "conversation" && (
          <ConversationTab
            scenario={scenario}
            run={run}
            messages={messages}
            failed={failed}
            loading={loading}
            onFix={onFix}
          />
        )}
        {tab === "assertions"  && <AssertionsTab verdicts={verdicts} hasRun={runId != null} />}
        {tab === "run_details" && <RunDetailsTab run={run} scenario={scenario} loading={loading} />}
        {tab === "mocks"       && <MocksTab skillId={skillId} scenario={scenario} />}
        {tab === "notes"       && <NotesTab scenario={scenario} />}
      </div>
    </div>
  )
}

function Header({ scenario }: { scenario: TestLabScenario }) {
  const status = scenario.latestRunStatus
  const isInt  = scenario.mode === "integration"
  return (
    <div className="border-b border-border p-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h2 className="truncate text-lg font-semibold text-app-text">{scenario.name || scenario.key}</h2>
          <span className={cn(
            "shrink-0 rounded-control border px-1.5 py-0.5 text-[9px] uppercase tracking-wide",
            isInt
              ? "border-sky-500/30 bg-sky-500/10 text-sky-300"
              : "border-app-muted/30 bg-white/5 text-app-muted",
          )}>
            {isInt ? "int" : "iso"}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-app-muted">
          <code className="rounded bg-white/5 px-1">{scenario.key}</code>
          {scenario.category && <span className="rounded-control border border-border px-1.5 py-0.5">{scenario.category}</span>}
          {scenario.scope && <span className="rounded-control border border-border px-1.5 py-0.5">{scenario.scope}</span>}
          {scenario.maxTurns && <span>max {scenario.maxTurns} turns</span>}
          {status && (
            <span className={cn(
              "uppercase tracking-wide",
              status === "passed"  && "text-status-success",
              status === "failed"  && "text-status-error",
              status === "errored" && "text-status-warning",
            )}>{status}</span>
          )}
        </div>
      </div>

      <div className="mt-3 grid gap-3 text-xs sm:grid-cols-2">
        {scenario.goal && (
          <div>
            <div className="mb-0.5 text-[10px] uppercase tracking-wide text-app-muted">Goal</div>
            <div className="text-app-text">{scenario.goal}</div>
          </div>
        )}
        {scenario.persona && (
          <div>
            <div className="mb-0.5 text-[10px] uppercase tracking-wide text-app-muted">Persona</div>
            <div className="text-app-text">{scenario.persona}</div>
          </div>
        )}
        {scenario.allowedToolCalls?.length > 0 && (
          <div className="sm:col-span-2">
            <div className="mb-0.5 text-[10px] uppercase tracking-wide text-app-muted">Allowed tool calls (ok if used, not required)</div>
            <div className="flex flex-wrap gap-1">
              {scenario.allowedToolCalls.map((t) => (
                <code key={t} className="rounded bg-white/5 px-1.5 py-0.5 text-[11px] text-app-text">{t}</code>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function ConversationTab({ scenario, run, messages, failed, loading, onFix }:
  {
    scenario: TestLabScenario
    run: TestRunDetail | null
    messages: ChatBubble[]
    failed: boolean
    loading: boolean
    onFix: () => Promise<void> | void
  }) {
  if (scenario.latestRunId == null) {
    return (
      <div className="p-6 text-center text-sm text-app-muted">
        This scenario hasn't been run yet. Click <span className="text-app-text">Run</span> to execute and capture the conversation.
      </div>
    )
  }
  if (loading && messages.length === 0) {
    return <div className="p-6 text-center text-sm text-app-muted">Loading conversation…</div>
  }
  return (
    <div className="space-y-4">
      {failed && <ForgeOnFailure judgeSummary={run?.summary ?? scenario.latestRunSummary} onFix={onFix} />}
      {messages.length === 0 ? (
        <div className="rounded-card border border-border bg-app-bg p-4 text-sm text-app-muted">
          No transcript available for this run.
        </div>
      ) : (
        messages.map((m) => <ChatMessageBubble key={m.id} msg={m} userSide="right" />)
      )}
    </div>
  )
}

function AssertionsTab({ verdicts, hasRun }: { verdicts: TestLabAssertionVerdict[]; hasRun: boolean }) {
  if (!hasRun) {
    return <div className="text-sm text-app-muted">Assertions populate after the first run.</div>
  }
  if (verdicts.length === 0) {
    return <div className="text-sm text-app-muted">No assertions on this scenario.</div>
  }
  return (
    <ol className="space-y-3">
      {verdicts.map((v, i) => (
        <li key={`${v.id}-${i}`} className="rounded-card border border-border bg-app-bg p-3">
          <div className="flex items-start gap-2">
            {v.passed ? (
              <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-status-success" />
            ) : (
              <XCircle size={16} className="mt-0.5 shrink-0 text-status-error" />
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="rounded-control border border-border bg-app-panel px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-app-muted">
                  {v.kind === "failure" ? "must NOT" : "must"}
                </span>
                <span className="text-sm text-app-text">{v.text}</span>
              </div>
              {v.reason && (
                <p className="mt-1.5 text-xs italic text-app-muted">{v.reason}</p>
              )}
            </div>
          </div>
        </li>
      ))}
    </ol>
  )
}

function RunDetailsTab({ run, scenario, loading }:
  { run: TestRunDetail | null; scenario: TestLabScenario; loading: boolean }) {
  const { props } = usePage<SharedProps>()
  const isAdmin = props.isAdmin === true

  if (scenario.latestRunId == null) {
    return <div className="text-sm text-app-muted">No runs yet.</div>
  }
  if (loading && !run) {
    return <div className="text-sm text-app-muted">Loading…</div>
  }
  const dur = run?.durationMs ?? scenario.latestRunDurationMs
  const cost = run?.costUsd ?? scenario.latestRunCostUsd
  const snapshot = run?.agentSnapshot
  const hasSnapshot = snapshot && Object.keys(snapshot).length > 0
  return (
    <div className="space-y-3 text-sm">
      <Row label="Status"     value={(run?.status ?? scenario.latestRunStatus ?? "—") as string} />
      <Row label="Duration"   value={dur != null ? `${(dur / 1000).toFixed(1)}s` : "—"} />
      {isAdmin && (
        <>
          <Row label="Cost"   value={cost != null ? `$${cost.toFixed(4)}` : "—"} />
          <Row label="Tokens" value={run ? `${run.inputTokens ?? 0} in / ${run.outputTokens ?? 0} out` : "—"} />
        </>
      )}
      <Row label="Started"    value={run?.startedAt ?? "—"} />
      <Row label="Finished"   value={run?.finishedAt ?? scenario.latestRunAt ?? "—"} />
      {run?.summary && (
        <div className="pt-3">
          <div className="mb-1 text-[10px] uppercase tracking-wide text-app-muted">Judge summary</div>
          <p className="whitespace-pre-wrap rounded-card border border-border bg-app-bg p-3 text-app-muted">{run.summary}</p>
        </div>
      )}
      {hasSnapshot && (
        <div className="pt-3">
          <div className="mb-1 text-[10px] uppercase tracking-wide text-app-muted">Agent context</div>
          <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-card border border-border bg-app-bg p-3 text-[11px] text-app-muted">
            {JSON.stringify(snapshot, null, 2)}
          </pre>
        </div>
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border/40 pb-1">
      <span className="text-[10px] uppercase tracking-wide text-app-muted">{label}</span>
      <span className="text-app-text">{value}</span>
    </div>
  )
}

// Per-scenario partner-API mock editor. The catalog default lives in
// Companies::Client::RED_TEAM_MOCK_PAYLOADS — JsonEditor lints as the
// operator types so typos surface inline instead of at save. Saved
// values override the catalog for this scenario only, keyed by
// CompanyEndpoint.key. Empty object falls back to the catalog default.
function MocksTab({ skillId, scenario }: { skillId: number; scenario: TestLabScenario }) {
  const initial = JSON.stringify(scenario.endpointMockOverrides ?? {}, null, 2)
  const [text, setText]       = useState<string>(initial)
  const [saving, setSaving]   = useState(false)
  const [status, setStatus]   = useState<{ kind: "ok" | "err"; msg: string } | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)

  useEffect(() => {
    setText(JSON.stringify(scenario.endpointMockOverrides ?? {}, null, 2))
    setStatus(null)
  }, [scenario.id, scenario.endpointMockOverrides])

  async function save() {
    setSaving(true)
    setStatus(null)
    try {
      JSON.parse(text || "{}")
    } catch (e) {
      setStatus({ kind: "err", msg: `Invalid JSON: ${(e as Error).message}` })
      setSaving(false)
      return
    }
    try {
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? ""
      const res = await fetch(`/skills/${skillId}/scenarios/${scenario.id}`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf },
        body: JSON.stringify({ endpoint_mock_overrides: text }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string }
        setStatus({ kind: "err", msg: err.error ?? `HTTP ${res.status}` })
      } else {
        setStatus({ kind: "ok", msg: "Saved." })
      }
    } catch (e) {
      setStatus({ kind: "err", msg: (e as Error).message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-start justify-between gap-3 text-xs text-app-muted">
        <span>
          JSON object keyed by <code className="rounded bg-white/5 px-1">endpoint.key</code>.
          Missing keys fall back to the catalog default.
        </span>
        <button
          type="button"
          onClick={() => setHelpOpen(true)}
          className="inline-flex shrink-0 items-center gap-1 rounded-control border border-border px-2 py-0.5 text-[11px] text-app-muted transition hover:bg-white/5 hover:text-app-text"
          title="What goes in this box?"
        >
          <Info size={12} strokeWidth={2} />
          What is this?
        </button>
      </div>

      <JsonEditor value={text} onChange={setText} minHeight="320px" />

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded-control border border-brand-red bg-brand-red px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-redDark disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save mocks"}
        </button>
        {status && (
          <span className={cn("text-xs", status.kind === "ok" ? "text-status-success" : "text-status-error")}>
            {status.msg}
          </span>
        )}
      </div>

      <MocksHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  )
}

// Plain-language explainer. Goal: an operator who's never edited a mock
// before should grok the structure in <30 seconds. Pulls active endpoints
// from /endpoints (Inertia JSON) so the table reflects this tenant's
// reality, not a hardcoded example.
function MocksHelpDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [endpoints, setEndpoints] = useState<Array<{ key: string; httpMethod: string; path: string; active: boolean }>>([])
  const [loading, setLoading]     = useState(false)

  useEffect(() => {
    if (!open) return
    setLoading(true)
    fetch("/endpoints", { credentials: "same-origin", headers: { "X-Inertia": "true", Accept: "application/json" } })
      .then((r) => r.json())
      .then((payload: { props?: { endpoints?: Array<Record<string, unknown>> } }) => {
        const list = (payload?.props?.endpoints ?? [])
          .map((e) => ({
            key:        String(e.key ?? ""),
            httpMethod: String(e.httpMethod ?? "GET"),
            path:       String(e.pathTemplate ?? e.path ?? ""),
            active:     true,
          }))
          .filter((e) => e.key)
        setEndpoints(list)
      })
      .catch(() => setEndpoints([]))
      .finally(() => setLoading(false))
  }, [open])

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <header className="flex items-start gap-3 border-b border-border px-6 py-4">
        <Info size={18} className="mt-0.5 shrink-0 text-app-muted" />
        <div className="flex-1">
          <h2 className="text-base font-semibold">What goes in the Mocks box?</h2>
          <p className="mt-0.5 text-xs text-app-muted">A pretend-answer the agent gets when it asks the partner during this test.</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="-mr-2 -mt-1 rounded-control p-1.5 text-app-subtle transition hover:bg-white/5 hover:text-app-text">
          <X size={16} />
        </button>
      </header>
      <div className="space-y-4 px-6 py-5 text-sm text-app-text">
        <section>
          <p>
            Imagine the agent has a phone book of partners it can call (each row in <strong>Endpoints</strong>).
            When a test runs, we don't actually call them — we hand back a pretend answer so the test is
            fast and predictable. <strong>That pretend answer is what you put here.</strong>
          </p>
        </section>

        <section>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-app-subtle">Active endpoints in this company</h3>
          <p className="text-xs text-app-muted">Use the <code className="rounded bg-white/5 px-1">key</code> column as the JSON key in your Mocks box.</p>
          {loading ? (
            <div className="mt-2 text-xs text-app-subtle">Loading…</div>
          ) : endpoints.length === 0 ? (
            <div className="mt-2 text-xs text-app-subtle">No active endpoints configured for this company.</div>
          ) : (
            <div className="mt-2 overflow-hidden rounded-control border border-border">
              <table className="w-full text-xs">
                <thead className="bg-white/[0.03] text-app-subtle">
                  <tr>
                    <th className="px-3 py-1.5 text-left font-medium">Key</th>
                    <th className="px-3 py-1.5 text-left font-medium">HTTP</th>
                    <th className="px-3 py-1.5 text-left font-medium">Path</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {endpoints.map((e) => (
                    <tr key={e.key}>
                      <td className="px-3 py-1.5 font-mono text-app-text">{e.key}</td>
                      <td className="px-3 py-1.5 font-mono text-app-muted">{e.httpMethod}</td>
                      <td className="px-3 py-1.5 font-mono text-app-muted">{e.path}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-app-subtle">Shape</h3>
          <pre className="mt-1 overflow-x-auto rounded-control border border-border bg-app-bg p-3 font-mono text-[11px] text-app-text">{`{
  "coin_balance": { "coins": 42, "usd": "0.84" },
  "rewards_summary": { "total_earned_usd": "12.00", "pending": [], "recent": [] }
}`}</pre>
        </section>

        <section>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-app-subtle">Rules of thumb</h3>
          <ul className="list-disc space-y-1 pl-5 text-xs text-app-muted">
            <li>Use the endpoint <strong>nickname / key</strong> — never the numeric id.</li>
            <li>Only list endpoints you want to override; everything else falls back to the catalog default.</li>
            <li>An empty object <code className="rounded bg-white/5 px-1">{`{}`}</code> means "use the defaults for everything."</li>
            <li>The shape of each value should match what the real endpoint would return.</li>
          </ul>
        </section>

        <section>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-app-subtle">Why bother?</h3>
          <p className="text-xs text-app-muted">
            To stage a specific situation. Want to test what the agent does when the user has zero coins?
            Mock <code className="rounded bg-white/5 px-1">coin_balance</code> with <code className="rounded bg-white/5 px-1">{`{"coins": 0}`}</code>.
            Want to test the "huge balance" path? Mock it with <code className="rounded bg-white/5 px-1">{`{"coins": 999999}`}</code>.
          </p>
        </section>
      </div>
      <footer className="flex justify-end border-t border-border px-6 py-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded-control border border-border px-4 py-1.5 text-sm text-app-text transition hover:bg-white/5"
        >
          Got it
        </button>
      </footer>
    </Dialog>
  )
}

function NotesTab({ scenario }: { scenario: TestLabScenario }) {
  return (
    <div className="space-y-3 text-sm text-app-muted">
      <p>
        Author notes and the attacker-script details for this scenario aren't surfaced here yet —
        edit the scenario directly to see them.
      </p>
      <p className="text-xs">
        <span className="text-app-text">Key:</span> <code className="rounded bg-white/5 px-1">{scenario.key}</code>
      </p>
    </div>
  )
}
